import { Container, type Application } from 'pixi.js';
import gsap from 'gsap';
import type { Scene } from './scene';

type SceneFactory = () => Scene;

/**
 * Kill every GSAP tween whose target is somewhere in a Pixi subtree. Walks
 * the container recursively and kills tweens for each node, its `position`,
 * `scale`, `skew`, and `pivot`. Without this, any tween still targeting a
 * node (or one of its transform properties) keeps firing after the scene
 * is destroyed and throws `Cannot set properties of null (setting 'y')`
 * when the backing Pixi object is torn down mid-frame.
 */
function killSubtreeTweens(node: Container): void {
  gsap.killTweensOf(node);
  // These are the GSAP-compatible sub-objects most tweens target.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n = node as any;
  if (n.position) gsap.killTweensOf(n.position);
  if (n.scale) gsap.killTweensOf(n.scale);
  if (n.skew) gsap.killTweensOf(n.skew);
  if (n.pivot) gsap.killTweensOf(n.pivot);
  // Recurse into children. `node.children` exists on any Pixi Container.
  const children = (node.children ?? []) as Container[];
  for (const c of children) killSubtreeTweens(c);
}

/**
 * Minimal scene manager. Phaser's scene.start(key, data) is replaced by
 * manager.start(factoryKey, data). Scenes register a factory; the manager
 * constructs a fresh scene instance on each start so exit cleanup is simple.
 */
export class SceneManager {
  private factories = new Map<string, SceneFactory>();
  private current: Scene | null = null;
  private currentKey: string | null = null;

  constructor(private app: Application) {
    // Per-frame update tick. GSAP has its own ticker; we only drive scene.update.
    app.ticker.add((ticker) => this.current?.update(ticker.deltaMS));
  }

  register(key: string, factory: SceneFactory): void {
    this.factories.set(key, factory);
  }

  async start(key: string, data?: unknown): Promise<void> {
    const factory = this.factories.get(key);
    if (!factory) {
      console.warn(`[SceneManager] No factory registered for "${key}"`);
      return;
    }
    if (this.current) {
      // Kill every tween on the subtree BEFORE onExit + destroy so nothing
      // tries to write to a torn-down node's .position.y on the next tick.
      killSubtreeTweens(this.current);
      this.current.onExit();
      // onExit may have spawned new tweens during cleanup — kill those too.
      killSubtreeTweens(this.current);
      this.app.stage.removeChild(this.current);
      this.current.destroy({ children: true });
    }
    const next = factory();
    next._bind(this.app);
    this.current = next;
    this.currentKey = key;
    this.app.stage.addChild(next);
    await next.onEnter(data);
  }

  getCurrentKey(): string | null {
    return this.currentKey;
  }
}
